import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import imageCompression from "browser-image-compression";
import { db } from "@/api/db";
import { toast } from "@/components/ui/use-toast";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import {
  UserCircle2, Briefcase, Brain, HeartHandshake, Heart,
  CheckCircle2, Loader2, Plus, Trash2, X, Pencil,
  ArrowLeft, ArrowRight, ChevronsUpDown, Check,
} from "lucide-react";
import {
  GENDER_OPTIONS,
  CIVIL_STATE_OPTIONS,
  CHILDREN_OPTIONS,
  IA_PROFICIENCY_OPTIONS,
  SENIORITY_OPTIONS,
  STATES,
  PURPOSE_QUESTIONS_ORDER,
  PURPOSE_QUESTION_LABELS,
} from "./constants";

// ── Transition screen data ──
const TRANSITIONS = [
  {
    icon: UserCircle2,
    title: () => "Complete seu perfil profissional",
    desc: "Para se candidatar às oportunidades, você precisa preencher seus dados. Leva cerca de 10 minutos.",
  },
  {
    icon: Briefcase,
    title: (name) => `Legal te conhecer, ${name}!`,
    desc: "Agora conte sua história profissional e o que te destaca.",
  },
  {
    icon: Brain,
    title: () => "O que você sabe fazer?",
    desc: "Idiomas, habilidades e ferramentas. Quanto mais completo, mais oportunidades.",
  },
  {
    icon: HeartHandshake,
    title: () => "Vida e propósito",
    desc: "Conhecer você além do currículo nos ajuda a encontrar oportunidades ideais.",
  },
  {
    icon: Heart,
    title: () => "Quase lá! Suas preferências",
    desc: "Defina o que procura: cargo, salário e onde quer trabalhar.",
  },
  {
    icon: CheckCircle2,
    title: () => "Perfil profissional cadastrado!",
    desc: "Tudo pronto. Explore vagas, trilhas de carreira e muito mais.",
  },
];

// ── Country codes for phone input ──
const COUNTRY_CODES = [
  { code: "+55", country: "Brasil", flag: "🇧🇷" },
  { code: "+1", country: "Estados Unidos", flag: "🇺🇸" },
  { code: "+351", country: "Portugal", flag: "🇵🇹" },
  { code: "+34", country: "Espanha", flag: "🇪🇸" },
  { code: "+44", country: "Reino Unido", flag: "🇬🇧" },
  { code: "+33", country: "França", flag: "🇫🇷" },
  { code: "+49", country: "Alemanha", flag: "🇩🇪" },
  { code: "+39", country: "Itália", flag: "🇮🇹" },
  { code: "+54", country: "Argentina", flag: "🇦🇷" },
  { code: "+56", country: "Chile", flag: "🇨🇱" },
  { code: "+57", country: "Colômbia", flag: "🇨🇴" },
  { code: "+52", country: "México", flag: "🇲🇽" },
  { code: "+81", country: "Japão", flag: "🇯🇵" },
  { code: "+61", country: "Austrália", flag: "🇦🇺" },
  { code: "+41", country: "Suíça", flag: "🇨🇭" },
];

// ── Suggested work equipment items ──
const EQUIPMENT_SUGGESTIONS = [
  "Notebook", "Monitor extra", "Webcam", "Headset", "Microfone",
  "Mesa digitalizadora", "Smartphone", "Impressora", "Ring Light", "Câmera",
];

// ── Animation variants ──
const fadeSlide = {
  initial: { opacity: 0, x: 20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -20 },
  transition: { duration: 0.3 },
};

// ── Helpers ──
function getInitials(name) {
  if (!name) return "U";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

function getFirstName(name) {
  if (!name) return "";
  return name.trim().split(/\s+/)[0];
}

function formatCityName(city) {
  if (!city) return "";
  const lowerCaseWords = ["de", "da", "das", "do", "dos", "e"];
  return city.toLowerCase().split(" ").map((word, i) =>
    lowerCaseWords.includes(word) && i !== 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)
  ).join(" ");
}

// ── MultiSelectSearchable (reused from ProfileSettings) ──
function MultiSelectSearchable({ options, selected, onChange, placeholder, allowCustom }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const selectedIds = new Set(selected.map(s => s.id).filter(Boolean));
  const selectedNames = new Set(selected.map(s => s.name?.toLowerCase()).filter(Boolean));
  const filtered = (options || []).filter(
    o => !selectedIds.has(o.id) && !selectedNames.has(o.name?.toLowerCase())
  );

  const addItem = (item) => { onChange([...selected, item]); setSearch(""); };
  const removeItem = (index) => { onChange(selected.filter((_, i) => i !== index)); };
  const addCustom = () => {
    const trimmed = search.trim();
    if (!trimmed || selectedNames.has(trimmed.toLowerCase())) return;
    onChange([...selected, { name: trimmed, custom: true }]);
    setSearch("");
  };

  const hasExactMatch = filtered.some(o => o.name?.toLowerCase() === search.trim().toLowerCase());

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex w-full min-h-[40px] flex-wrap items-center gap-1 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          {selected.length === 0 && (
            <span className="text-muted-foreground">{placeholder || "Adicionar..."}</span>
          )}
          {selected.map((item, i) => (
            <Badge key={i} variant="secondary" className="gap-1">
              {item.name}
              <X className="w-3 h-3 cursor-pointer hover:text-destructive"
                onClick={(e) => { e.stopPropagation(); removeItem(i); }} />
            </Badge>
          ))}
          <ChevronsUpDown className="ml-auto h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Buscar..." value={search} onValueChange={setSearch} />
          <CommandList>
            <CommandEmpty>
              {allowCustom && search.trim() ? (
                <button type="button" className="w-full px-2 py-1.5 text-sm text-left hover:bg-accent rounded cursor-pointer" onClick={addCustom}>
                  <Plus className="w-3 h-3 inline mr-1" />Criar "{search.trim()}"
                </button>
              ) : "Nenhum resultado."}
            </CommandEmpty>
            <CommandGroup>
              {filtered
                .filter(o => !search || o.name?.toLowerCase().includes(search.toLowerCase()))
                .map(opt => (
                  <CommandItem key={opt.id} value={opt.name} onSelect={() => addItem({ id: opt.id, name: opt.name })}>
                    {opt.name}
                  </CommandItem>
                ))}
              {allowCustom && search.trim() && !hasExactMatch && filtered.filter(o => o.name?.toLowerCase().includes(search.toLowerCase())).length > 0 && (
                <CommandItem onSelect={addCustom}>
                  <Plus className="w-3 h-3 mr-1" />Criar "{search.trim()}"
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ── RegionAdder (reused from ProfileSettings) ──
let citiesJsonCache = null;

function RegionAdder({ onAdd }) {
  const [selectedState, setSelectedState] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedCityCode, setSelectedCityCode] = useState("");
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [citySearch, setCitySearch] = useState("");
  const [cityPopoverOpen, setCityPopoverOpen] = useState(false);

  useEffect(() => {
    if (!selectedState) { setCities([]); setSelectedCity(""); setSelectedCityCode(""); return; }
    setSelectedCity(""); setSelectedCityCode(""); setCitySearch("");
    if (citiesJsonCache) { setCities(citiesJsonCache[selectedState] || []); return; }
    setLoadingCities(true);
    fetch('/cities.json').then(r => r.json()).then(data => {
      citiesJsonCache = data;
      setCities(data[selectedState] || []);
    }).catch(() => setCities([])).finally(() => setLoadingCities(false));
  }, [selectedState]);

  const handleAdd = () => {
    if (!selectedState) return;
    onAdd({ state: selectedState, city: selectedCity || '', cityCode: selectedCityCode || '' });
    setSelectedState(""); setSelectedCity(""); setSelectedCityCode(""); setCitySearch("");
  };

  const filteredCities = citySearch
    ? cities.filter(c => c.nome.toLowerCase().includes(citySearch.toLowerCase())).slice(0, 50)
    : cities.slice(0, 50);

  return (
    <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3">
      <div className="flex-1 space-y-1">
        <Label className="text-xs">Estado</Label>
        <Select value={selectedState} onValueChange={v => setSelectedState(v)}>
          <SelectTrigger><SelectValue placeholder="Selecione o estado" /></SelectTrigger>
          <SelectContent>
            {STATES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="flex-1 space-y-1">
        <Label className="text-xs">Cidade</Label>
        <Popover open={cityPopoverOpen} onOpenChange={setCityPopoverOpen}>
          <PopoverTrigger asChild>
            <button type="button" disabled={!selectedState || loadingCities}
              className="flex w-full min-h-[40px] items-center rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed">
              <span className={selectedCity ? "text-foreground" : "text-muted-foreground"}>
                {loadingCities ? "Carregando..." : selectedCity ? formatCityName(selectedCity) : "Buscar cidade..."}
              </span>
              <ChevronsUpDown className="ml-auto h-4 w-4 shrink-0 opacity-50" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
            <Command shouldFilter={false}>
              <CommandInput placeholder="Buscar cidade..." value={citySearch} onValueChange={setCitySearch} />
              <CommandList>
                <CommandEmpty>Nenhuma cidade encontrada.</CommandEmpty>
                <CommandGroup>
                  {filteredCities.map(c => (
                    <CommandItem key={c.codigo_ibge} value={c.nome}
                      onSelect={() => { setSelectedCity(c.nome); setSelectedCityCode(c.codigo_ibge); setCityPopoverOpen(false); setCitySearch(""); }}>
                      <Check className={`mr-2 h-4 w-4 ${selectedCity === c.nome ? "opacity-100" : "opacity-0"}`} />
                      {formatCityName(c.nome)}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
      <Button type="button" variant="outline" size="sm" onClick={handleAdd} disabled={!selectedState} className="shrink-0">
        <Plus className="w-4 h-4 mr-1" /> Adicionar região
      </Button>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// ══  MAIN COMPONENT  ═════════════════════════════════════
// ══════════════════════════════════════════════════════════

export default function ProfileOnboarding({ profile: initialProfile, onComplete }) {
  const draftKey = `portus_onboarding_draft_${initialProfile.id}`;

  // ── Restore draft from localStorage ──
  const savedDraft = (() => {
    try {
      const raw = localStorage.getItem(draftKey);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  })();

  // step: 1-5, phase: "transition" | "form"
  // step 0 = welcome, step 6 = completion
  const [step, setStep] = useState(savedDraft?.step || 0);
  const [phase, setPhase] = useState(savedDraft?.step ? "transition" : "transition");
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(initialProfile);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarInputRef = useRef(null);
  const savingRef = useRef(false);

  const lookups = profile.lookups || {};
  const firstName = getFirstName(profile.fullName);

  // ── Build default form data from profile ──
  const buildDefaultFormData = () => {
    const p = initialProfile;
    const k = p.knowledge || {};

    let phoneCountryCode = "+55";
    let phoneNumber = p.whatsapp || "";
    if (phoneNumber) {
      const match = COUNTRY_CODES.find(c => phoneNumber.startsWith(c.code));
      if (match) {
        phoneCountryCode = match.code;
        phoneNumber = phoneNumber.slice(match.code.length);
      }
    }

    return {
      // Step 1 — Personal
      fullName: p.fullName || "",
      gender: p.gender || "",
      birthDate: p.birthDate ? p.birthDate.substring(0, 10) : "",
      phoneCountryCode,
      phoneNumber,
      nationalityId: p.nationalityId || "",
      children: p.children != null ? String(p.children) : "",
      civilState: p.civilState || "",
      workEquipments: p.workEquipments || [],
      workEquipmentInput: "",
      address: {
        zipCode: p.addresses?.zipCode || "",
        street: p.addresses?.street || "",
        streetNumber: p.addresses?.streetNumber || "",
        complement: p.addresses?.complement || "",
        neighborhood: p.addresses?.neighborhood || "",
        city: p.addresses?.city || "",
        state: p.addresses?.state || "",
      },
      // Step 2 — Experience
      professionalPitch: p.professionalPitch || "",
      historyEntries: (p.professionalHistory || []).map(h => ({
        id: h.id || null,
        companyName: h.companyName || "",
        jobPositionId: h.jobPositionId || "",
        jobPosition: h.jobPosition || "",
        startDate: h.startDate ? h.startDate.substring(0, 10) : "",
        endDate: h.endDate ? h.endDate.substring(0, 10) : "",
        description: h.description || "",
        currentJob: h.currentJob || false,
      })),
      // Step 3 — Knowledge
      languages: (k.languages || []).map(l => ({ ...l })),
      skills: [
        ...(k.skills || []).map(s => ({ id: s.id, name: s.name })),
        ...(k.customSkills || []).map(s => ({ name: s.name || s, custom: true })),
      ],
      tools: [
        ...(k.tools || []).map(t => ({ id: t.id, name: t.name })),
        ...(k.customTools || []).map(t => ({ name: t.name || t, custom: true })),
      ],
      iaProficiency: k.iaProficiency || p.iaProficiency || "",
      // Step 4 — Purpose
      purposeQuestions: PURPOSE_QUESTIONS_ORDER.map(qKey => {
        const existing = (p.purposeQuestions || []).find(q => q.purposeQuestion === qKey);
        return existing
          ? { ...existing }
          : { purposeQuestion: qKey, response: "" };
      }),
      // Step 5 — Preferences
      preferredPositions: (p.preferredPositions || []).map(pp => ({ ...pp })),
      minSalary: p.minSalary ?? "",
      maxSalary: p.maxSalary ?? "",
      contractModelIds: (p.contractModels || []).map(c => c.id),
      availabilityIds: (p.availability || []).map(a => a.id),
      jobModelIds: (p.jobModels || []).map(j => j.id),
      preferredRegions: (p.preferredRegions || []).map(r => ({
        state: r.state, city: r.city || '', cityCode: r.cityCode || '',
      })),
    };
  };

  // ── Form data: merge saved draft over defaults ──
  const [formData, setFormData] = useState(() => {
    const defaults = buildDefaultFormData();
    if (!savedDraft?.formData) return defaults;
    // Merge draft over defaults (draft wins, but keep structure)
    return { ...defaults, ...savedDraft.formData, workEquipmentInput: "" };
  });

  const updateForm = (key, value) => setFormData(prev => ({ ...prev, [key]: value }));
  const updateFormNested = (parent, key, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: { ...(prev[parent] || {}), [key]: value },
    }));
  };

  // ── Persist draft to localStorage ──
  const saveDraft = (nextStep, data) => {
    try {
      const draft = { step: nextStep, formData: { ...data, workEquipmentInput: "" } };
      localStorage.setItem(draftKey, JSON.stringify(draft));
    } catch { /* storage full — ignore */ }
  };

  const clearDraft = () => {
    try { localStorage.removeItem(draftKey); } catch {}
  };

  // ── CEP auto-fill ──
  const handleCepBlur = async () => {
    const cep = formData.address?.zipCode?.replace(/\D/g, "");
    if (!cep || cep.length !== 8) return;
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await res.json();
      if (data.erro) return;
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          street: data.logradouro || prev.address.street,
          neighborhood: data.bairro || prev.address.neighborhood,
          city: data.localidade || prev.address.city,
          state: data.uf || prev.address.state,
        },
      }));
    } catch { /* ignore */ }
  };

  // ── Avatar upload ──
  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAvatar(true);
    try {
      const compressed = await imageCompression(file, {
        maxSizeMB: 1, maxWidthOrHeight: 2000, initialQuality: 0.9, useWebWorker: true,
      });
      const { avatarUrl } = await db.profile.uploadAvatar(compressed);
      setProfile(prev => ({ ...prev, avatarUrl }));
      window.dispatchEvent(new CustomEvent("avatar-updated", { detail: { avatarUrl } }));
      toast({ title: "Foto atualizada!" });
    } catch (err) {
      toast({ title: "Erro ao enviar foto", description: err.message, variant: "destructive" });
    } finally {
      setUploadingAvatar(false);
      if (avatarInputRef.current) avatarInputRef.current.value = "";
    }
  };

  // ── Build FULL payload with ALL steps data (save everything at once) ──
  const buildFullPayload = () => {
    // Step 1 — Personal
    const payload = {
      fullName: formData.fullName,
      gender: formData.gender || undefined,
      nationalityId: formData.nationalityId || undefined,
      children: formData.children !== "" ? formData.children === "true" : undefined,
      civilState: formData.civilState || undefined,
      birthDate: formData.birthDate || undefined,
      whatsapp: formData.phoneNumber ? formData.phoneCountryCode + formData.phoneNumber : "",
      workEquipments: formData.workEquipments || [],
    };
    const addr = formData.address;
    const hasAddr = addr && Object.values(addr).some(v => v);
    if (hasAddr) payload.address = { ...addr };

    // Step 2 — Experience
    payload.professionalPitch = formData.professionalPitch || "";
    payload.professionalHistories = (formData.historyEntries || [])
      .filter(h => h.companyName && h.jobPositionId && h.startDate)
      .map(h => ({
        id: h.id || undefined,
        companyName: h.companyName,
        jobPositionId: h.jobPositionId,
        startDate: h.startDate,
        endDate: h.endDate || null,
        description: h.description || "",
        currentJob: h.currentJob ?? false,
      }));

    // Step 3 — Knowledge
    const predefinedSkills = (formData.skills || []).filter(s => s.id);
    const customSkills = (formData.skills || []).filter(s => !s.id || s.custom);
    const predefinedTools = (formData.tools || []).filter(t => t.id);
    const customTools = (formData.tools || []).filter(t => !t.id || t.custom);
    payload.skillsIds = predefinedSkills.map(s => s.id);
    payload.customSkills = customSkills.map(s => s.name).filter(Boolean);
    payload.jobToolsIds = predefinedTools.map(t => t.id);
    payload.customTools = customTools.map(t => t.name).filter(Boolean);
    payload.languages = (formData.languages || [])
      .filter(l => l.languageId && l.fluencyLevelId)
      .map(l => ({ id: l.id || undefined, languageId: l.languageId, fluencyLevelId: l.fluencyLevelId }));
    payload.iaProficiency = formData.iaProficiency || "NONE";

    // Step 4 — Purpose
    payload.purposeQuestions = (formData.purposeQuestions || [])
      .filter(q => q.response)
      .map(q => q.id
        ? { id: q.id, response: q.response || "" }
        : { purposeQuestion: q.purposeQuestion, response: q.response || "" }
      );

    // Step 5 — Preferences
    payload.minSalary = formData.minSalary !== "" ? Number(formData.minSalary) : 0;
    payload.maxSalary = formData.maxSalary !== "" ? Number(formData.maxSalary) : 0;
    payload.preferredPositions = (formData.preferredPositions || [])
      .filter(pp => pp.jobPositionId)
      .map(pp => ({ jobPositionId: pp.jobPositionId, seniority: pp.seniority || "PLENO" }));
    payload.contractModelIds = formData.contractModelIds || [];
    payload.availabilityIds = formData.availabilityIds || [];
    payload.jobModelIds = formData.jobModelIds || [];
    payload.preferredRegions = (formData.preferredRegions || []).map(r => ({
      state: r.state, city: r.city || '', cityCode: r.cityCode || '',
    }));

    return payload;
  };

  // ── Validation per step ──
  const isStepValid = (currentStep) => {
    switch (currentStep) {
      case 1:
        return !!(formData.gender && formData.birthDate && formData.phoneNumber && formData.address?.zipCode);
      case 2:
        return !!(formData.professionalPitch &&
          (formData.historyEntries || []).some(h => h.companyName && h.jobPositionId && h.startDate));
      case 3:
        return (formData.skills || []).length > 0;
      case 4:
        return (formData.purposeQuestions || []).some(q => q.response);
      case 5:
        return !!((formData.preferredPositions || []).some(pp => pp.jobPositionId) &&
          formData.minSalary !== "" && formData.maxSalary !== "");
      default:
        return true;
    }
  };

  // ── Advance step (save only on final step — all data at once) ──
  const handleNext = async () => {
    // Welcome screen → go straight to step 1 form (no transition)
    if (step === 0) {
      setStep(1);
      setPhase("form");
      saveDraft(1, formData);
      return;
    }

    // Transition → form
    if (phase === "transition") {
      setPhase("form");
      return;
    }

    // Steps 1-4: just advance locally (no API call), persist draft
    if (step < 5) {
      const nextStep = step + 1;
      setStep(nextStep);
      setPhase("transition");
      saveDraft(nextStep, formData);
      return;
    }

    // Step 5 (final): save ALL data at once, then show completion
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);

    try {
      const payload = buildFullPayload();
      const updated = await db.profile.update(payload);
      setProfile(updated);
      clearDraft();
      setStep(6);
      setPhase("transition");
    } catch (err) {
      toast({ title: "Erro ao salvar", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
      savingRef.current = false;
    }
  };

  const handleBack = () => {
    if (phase === "form") {
      if (step === 1) {
        // Step 1 has no transition before it — go back to welcome
        setStep(0);
        setPhase("transition");
      } else {
        // Steps 2-5 have a transition before the form
        setPhase("transition");
      }
      return;
    }
    // phase === "transition" (steps 2-5) — go back to previous step's form
    if (step >= 2) {
      setStep(step - 1);
      setPhase("form");
    }
  };

  const handleFinish = () => {
    clearDraft();
    onComplete(profile);
  };

  // ── Step Indicator ──
  const StepIndicator = () => (
    <div className="flex items-center justify-center gap-2 mb-6">
      {[1, 2, 3, 4, 5].map(s => {
        const isCompleted = s < step || (s === step && step === 6);
        const isActive = s === step;
        return (
          <div
            key={s}
            className="flex items-center justify-center rounded-full font-semibold text-sm transition-all duration-300"
            style={{
              width: 40, height: 40,
              background: isCompleted ? "#00DCA4" : isActive ? "#00DCA4" : "#e5e7eb",
              color: isCompleted || isActive ? "#003326" : "#9ca3af",
            }}
          >
            {isCompleted ? <Check className="w-5 h-5" /> : s}
          </div>
        );
      })}
    </div>
  );

  // ── Transition Screen ──
  const TransitionScreen = ({ transitionIndex }) => {
    const t = TRANSITIONS[transitionIndex];
    const Icon = t.icon;
    return (
      <motion.div {...fadeSlide} key={`transition-${transitionIndex}`}
        className="flex flex-col items-center justify-center text-center py-12 px-6">
        <div className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
          style={{ background: "#B0FCEA" }}>
          <Icon className="w-10 h-10" style={{ color: "#00664C" }} />
        </div>
        <h2 className="text-2xl font-bold mb-3" style={{ color: "#003326" }}>
          {t.title(firstName)}
        </h2>
        <p className="text-base max-w-md" style={{ color: "#646C6F" }}>{t.desc}</p>
      </motion.div>
    );
  };

  // ══════════════════════════════════════════════════
  // ══  STEP FORMS  ═════════════════════════════════
  // ══════════════════════════════════════════════════

  const Step1Personal = () => (
    <motion.div {...fadeSlide} key="step1" className="space-y-4">
      <h3 className="text-lg font-semibold" style={{ color: "#003326" }}>Dados Pessoais</h3>

      {/* Avatar */}
      <div className="flex items-center gap-4">
        <div className="relative group">
          <Avatar className="w-16 h-16">
            {profile.avatarUrl && <AvatarImage src={profile.avatarUrl} alt={formData.fullName} />}
            <AvatarFallback className="text-xl font-bold" style={{ background: "#B0FCEA", color: "#00664C" }}>
              {getInitials(formData.fullName)}
            </AvatarFallback>
          </Avatar>
          <button type="button"
            className="absolute inset-0 rounded-full flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
            onClick={() => avatarInputRef.current?.click()} disabled={uploadingAvatar}>
            {uploadingAvatar ? <Loader2 className="w-4 h-4 animate-spin text-white" /> : <Pencil className="w-4 h-4 text-white" />}
          </button>
          <input ref={avatarInputRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarUpload} />
        </div>
        <div className="text-sm text-muted-foreground">Foto de perfil (opcional)</div>
      </div>

      {/* Name */}
      <div className="space-y-2">
        <Label>Nome completo</Label>
        <Input value={formData.fullName || ""} onChange={e => updateForm("fullName", e.target.value)} />
      </div>

      {/* Gender + Birth */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Gênero *</Label>
          <Select value={formData.gender || ""} onValueChange={v => updateForm("gender", v)}>
            <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
            <SelectContent>
              {GENDER_OPTIONS.map(g => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Data de nascimento *</Label>
          <Input type="date" value={formData.birthDate || ""} onChange={e => updateForm("birthDate", e.target.value)} />
        </div>
      </div>

      {/* WhatsApp */}
      <div className="space-y-2">
        <Label>WhatsApp *</Label>
        <div className="flex gap-2">
          <Select value={formData.phoneCountryCode} onValueChange={v => updateForm("phoneCountryCode", v)}>
            <SelectTrigger className="w-[100px] shrink-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {COUNTRY_CODES.map(c => (
                <SelectItem key={c.code} value={c.code}>
                  {c.flag} {c.code}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            className="flex-1"
            placeholder="11999999999"
            value={formData.phoneNumber || ""}
            onChange={e => updateForm("phoneNumber", e.target.value.replace(/\D/g, ""))}
          />
        </div>
      </div>

      {/* Nationality + Children + CivilState */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Nacionalidade</Label>
          <Select value={formData.nationalityId || ""} onValueChange={v => updateForm("nationalityId", v)}>
            <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
            <SelectContent>
              {(lookups.nationalities || []).map(n => <SelectItem key={n.id} value={n.id}>{n.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Filhos</Label>
          <Select value={formData.children || ""} onValueChange={v => updateForm("children", v)}>
            <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
            <SelectContent>
              {CHILDREN_OPTIONS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Estado civil</Label>
          <Select value={formData.civilState || ""} onValueChange={v => updateForm("civilState", v)}>
            <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
            <SelectContent>
              {CIVIL_STATE_OPTIONS.map(cs => <SelectItem key={cs.value} value={cs.value}>{cs.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Work Equipments — dropdown with suggestions + custom */}
      <div className="space-y-2">
        <Label>Equipamentos de trabalho</Label>
        <MultiSelectSearchable
          options={EQUIPMENT_SUGGESTIONS.map(name => ({ id: name, name }))}
          selected={(formData.workEquipments || []).map(name => ({ id: name, name }))}
          onChange={items => updateForm("workEquipments", items.map(i => i.name))}
          placeholder="Selecione ou digite equipamentos..."
          allowCustom
        />
      </div>

      {/* Address */}
      <Separator />
      <p className="text-sm font-medium text-muted-foreground">Endereço</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>CEP *</Label>
          <Input value={formData.address?.zipCode || ""}
            onChange={e => updateFormNested("address", "zipCode", e.target.value)}
            onBlur={handleCepBlur} />
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label>Rua</Label>
          <Input value={formData.address?.street || ""} onChange={e => updateFormNested("address", "street", e.target.value)} />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Número</Label>
          <Input value={formData.address?.streetNumber || ""} onChange={e => updateFormNested("address", "streetNumber", e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label>Complemento</Label>
          <Input value={formData.address?.complement || ""} onChange={e => updateFormNested("address", "complement", e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label>Bairro</Label>
          <Input value={formData.address?.neighborhood || ""} onChange={e => updateFormNested("address", "neighborhood", e.target.value)} />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Cidade</Label>
          <Input value={formData.address?.city || ""} onChange={e => updateFormNested("address", "city", e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label>Estado</Label>
          <Input value={formData.address?.state || ""} onChange={e => updateFormNested("address", "state", e.target.value)} />
        </div>
      </div>
    </motion.div>
  );

  const Step2Experience = () => (
    <motion.div {...fadeSlide} key="step2" className="space-y-4">
      <h3 className="text-lg font-semibold" style={{ color: "#003326" }}>Experiência Profissional</h3>

      {/* Pitch */}
      <div className="space-y-2">
        <Label>Pitch profissional *</Label>
        <RichTextEditor
          value={formData.professionalPitch || ""}
          onChange={val => updateForm("professionalPitch", val)}
          placeholder="Descreva brevemente seu perfil profissional..."
        />
      </div>

      <Separator />

      {/* Professional History */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium">Histórico profissional *</Label>
          <Button type="button" variant="outline" size="sm"
            onClick={() => updateForm("historyEntries", [...(formData.historyEntries || []), {
              companyName: "", jobPositionId: "", jobPosition: "", startDate: "", endDate: "", description: "", currentJob: false,
            }])}>
            <Plus className="w-4 h-4 mr-1" /> Adicionar
          </Button>
        </div>

        {(formData.historyEntries || []).length === 0 && (
          <p className="text-sm text-muted-foreground">Adicione pelo menos uma experiência profissional.</p>
        )}

        {(formData.historyEntries || []).map((entry, i) => (
          <div key={i} className="relative border rounded-lg p-4 space-y-4">
            <Button variant="ghost" size="icon"
              className="absolute top-2 right-2 text-muted-foreground hover:text-destructive"
              onClick={() => updateForm("historyEntries", formData.historyEntries.filter((_, j) => j !== i))}>
              <Trash2 className="w-4 h-4" />
            </Button>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Empresa</Label>
                <Input value={entry.companyName || ""} onChange={e => {
                  const updated = [...formData.historyEntries]; updated[i] = { ...updated[i], companyName: e.target.value };
                  updateForm("historyEntries", updated);
                }} />
              </div>
              <div className="space-y-2">
                <Label>Cargo</Label>
                <Select value={entry.jobPositionId || ""} onValueChange={v => {
                  const opt = lookups.positions?.find(p => p.id === v);
                  const updated = [...formData.historyEntries]; updated[i] = { ...updated[i], jobPositionId: v, jobPosition: opt?.name || "" };
                  updateForm("historyEntries", updated);
                }}>
                  <SelectTrigger><SelectValue placeholder="Selecione o cargo">{entry.jobPosition || "Selecione o cargo"}</SelectValue></SelectTrigger>
                  <SelectContent>
                    {(lookups.positions || []).map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
              <div className="space-y-2">
                <Label>Data de início</Label>
                <Input type="date" value={entry.startDate || ""} onChange={e => {
                  const updated = [...formData.historyEntries]; updated[i] = { ...updated[i], startDate: e.target.value };
                  updateForm("historyEntries", updated);
                }} />
              </div>
              <div className="space-y-2">
                <Label>Data de término</Label>
                <Input type="date" disabled={entry.currentJob} value={entry.endDate || ""} onChange={e => {
                  const updated = [...formData.historyEntries]; updated[i] = { ...updated[i], endDate: e.target.value || null };
                  updateForm("historyEntries", updated);
                }} />
              </div>
              <label className="flex items-center gap-2 cursor-pointer pb-1">
                <input type="checkbox" checked={!!entry.currentJob} onChange={e => {
                  const updated = [...formData.historyEntries];
                  updated[i] = { ...updated[i], currentJob: e.target.checked, endDate: e.target.checked ? "" : updated[i].endDate };
                  updateForm("historyEntries", updated);
                }} className="w-4 h-4 rounded border-gray-300" style={{ accentColor: "#00DCA4" }} />
                <span className="text-sm">Trabalho atual</span>
              </label>
            </div>

            <div className="space-y-2">
              <Label>Descrição</Label>
              <RichTextEditor value={entry.description || ""} onChange={val => {
                const updated = [...formData.historyEntries]; updated[i] = { ...updated[i], description: val };
                updateForm("historyEntries", updated);
              }} placeholder="Descreva suas responsabilidades e realizações..." maxLength={250} />
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );

  const Step3Knowledge = () => (
    <motion.div {...fadeSlide} key="step3" className="space-y-6">
      <h3 className="text-lg font-semibold" style={{ color: "#003326" }}>Conhecimentos</h3>

      {/* Languages */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium">Idiomas</Label>
          <Button type="button" variant="outline" size="sm"
            onClick={() => updateForm("languages", [...(formData.languages || []), { languageId: "", language: "", fluencyLevelId: "", fluencyLevel: "" }])}>
            <Plus className="w-4 h-4 mr-1" /> Adicionar
          </Button>
        </div>
        {(formData.languages || []).map((lang, i) => (
          <div key={i} className="flex items-center gap-3">
            <Select value={lang.languageId || ""} onValueChange={v => {
              const opt = lookups.languages?.find(l => l.id === v);
              const updated = [...formData.languages]; updated[i] = { ...updated[i], languageId: v, language: opt?.name || "" };
              updateForm("languages", updated);
            }}>
              <SelectTrigger className="flex-1"><SelectValue placeholder="Idioma">{lang.language || "Idioma"}</SelectValue></SelectTrigger>
              <SelectContent>{(lookups.languages || []).map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={lang.fluencyLevelId || ""} onValueChange={v => {
              const opt = lookups.fluencyLevels?.find(f => f.id === v);
              const updated = [...formData.languages]; updated[i] = { ...updated[i], fluencyLevelId: v, fluencyLevel: opt?.name || "" };
              updateForm("languages", updated);
            }}>
              <SelectTrigger className="w-40"><SelectValue placeholder="Fluência">{lang.fluencyLevel || "Fluência"}</SelectValue></SelectTrigger>
              <SelectContent>{(lookups.fluencyLevels || []).map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
            </Select>
            <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-destructive shrink-0"
              onClick={() => updateForm("languages", formData.languages.filter((_, j) => j !== i))}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </div>

      <Separator />

      {/* Skills */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Habilidades *</Label>
        <MultiSelectSearchable options={lookups.skills || []} selected={formData.skills || []}
          onChange={val => updateForm("skills", val)} placeholder="Buscar ou criar habilidades..." allowCustom />
      </div>

      <Separator />

      {/* Tools */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Ferramentas</Label>
        <MultiSelectSearchable options={lookups.tools || []} selected={formData.tools || []}
          onChange={val => updateForm("tools", val)} placeholder="Buscar ou criar ferramentas..." allowCustom />
      </div>

      <Separator />

      {/* IA Proficiency */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Proficiência em IA</Label>
        <Select value={formData.iaProficiency || ""} onValueChange={v => updateForm("iaProficiency", v)}>
          <SelectTrigger className="w-full sm:w-64"><SelectValue placeholder="Selecione" /></SelectTrigger>
          <SelectContent>
            {IA_PROFICIENCY_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </motion.div>
  );

  const Step4Purpose = () => (
    <motion.div {...fadeSlide} key="step4" className="space-y-6">
      <h3 className="text-lg font-semibold" style={{ color: "#003326" }}>Vida e Propósito</h3>
      <p className="text-sm text-muted-foreground">Responda pelo menos uma pergunta.</p>

      {PURPOSE_QUESTIONS_ORDER.map(qKey => {
        const q = (formData.purposeQuestions || []).find(q => q.purposeQuestion === qKey);
        return (
          <div key={qKey} className="space-y-2">
            <Label className="text-sm font-medium">{PURPOSE_QUESTION_LABELS[qKey]}</Label>
            <RichTextEditor
              value={q?.response || ""}
              onChange={val => {
                const updated = (formData.purposeQuestions || []).map(item =>
                  item.purposeQuestion === qKey ? { ...item, response: val } : item
                );
                updateForm("purposeQuestions", updated);
              }}
              placeholder="Escreva sua resposta..."
            />
          </div>
        );
      })}
    </motion.div>
  );

  const Step5Preferences = () => {
    const selectedJobModelIds = formData.jobModelIds || [];
    const showRegions = (lookups.jobModels || []).some(
      jm => selectedJobModelIds.includes(jm.id) && ['HYBRID', 'IN_PERSON'].includes(jm.workModality)
    );

    return (
      <motion.div {...fadeSlide} key="step5" className="space-y-4">
        <h3 className="text-lg font-semibold" style={{ color: "#003326" }}>Preferências</h3>

        {/* Preferred Positions */}
        <div className="space-y-2">
          <Label>Cargos de interesse *</Label>
          {(formData.preferredPositions || []).map((pp, i) => (
            <div key={i} className="flex gap-2 items-center">
              <Select value={pp.jobPositionId || ""} onValueChange={v => {
                const updated = [...formData.preferredPositions]; updated[i] = { ...updated[i], jobPositionId: v };
                updateForm("preferredPositions", updated);
              }}>
                <SelectTrigger className="flex-1"><SelectValue placeholder="Cargo" /></SelectTrigger>
                <SelectContent>{(lookups.positions || []).map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={pp.seniority || "PLENO"} onValueChange={v => {
                const updated = [...formData.preferredPositions]; updated[i] = { ...updated[i], seniority: v };
                updateForm("preferredPositions", updated);
              }}>
                <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                <SelectContent>{SENIORITY_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
              <Button variant="ghost" size="icon"
                onClick={() => updateForm("preferredPositions", formData.preferredPositions.filter((_, j) => j !== i))}>
                <Trash2 className="w-4 h-4 text-destructive" />
              </Button>
            </div>
          ))}
          <Button variant="outline" size="sm"
            onClick={() => updateForm("preferredPositions", [...(formData.preferredPositions || []), { jobPositionId: "", seniority: "PLENO" }])}>
            <Plus className="w-4 h-4 mr-1" /> Adicionar cargo
          </Button>
        </div>

        {/* Salary Range */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Salário mínimo (R$) *</Label>
            <Input type="number" placeholder="5000" value={formData.minSalary ?? ""}
              onChange={e => updateForm("minSalary", e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Salário máximo (R$) *</Label>
            <Input type="number" placeholder="10000" value={formData.maxSalary ?? ""}
              onChange={e => updateForm("maxSalary", e.target.value)} />
          </div>
        </div>

        {/* Contract Models */}
        <div className="space-y-2">
          <Label>Formato de contratação</Label>
          <div className="flex flex-wrap gap-2">
            {(lookups.contractModels || []).map(cm => {
              const selected = (formData.contractModelIds || []).includes(cm.id);
              return (
                <Badge key={cm.id} variant={selected ? "default" : "outline"} className="cursor-pointer"
                  onClick={() => {
                    const ids = formData.contractModelIds || [];
                    updateForm("contractModelIds", selected ? ids.filter(id => id !== cm.id) : [...ids, cm.id]);
                  }}>{cm.name}</Badge>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Availability */}
          <div className="space-y-2">
            <Label>Disponibilidade</Label>
            <div className="flex flex-wrap gap-2">
              {(lookups.availability || []).map(av => {
                const selected = (formData.availabilityIds || []).includes(av.id);
                return (
                  <Badge key={av.id} variant={selected ? "default" : "outline"} className="cursor-pointer"
                    onClick={() => {
                      const ids = formData.availabilityIds || [];
                      updateForm("availabilityIds", selected ? ids.filter(id => id !== av.id) : [...ids, av.id]);
                    }}>{av.name}</Badge>
                );
              })}
            </div>
          </div>

          {/* Job Models */}
          <div className="space-y-2">
            <Label>Formato de trabalho</Label>
            <div className="flex flex-wrap gap-2">
              {(lookups.jobModels || []).map(jm => {
                const selected = selectedJobModelIds.includes(jm.id);
                return (
                  <Badge key={jm.id} variant={selected ? "default" : "outline"} className="cursor-pointer"
                    onClick={() => {
                      const ids = formData.jobModelIds || [];
                      const newIds = selected ? ids.filter(id => id !== jm.id) : [...ids, jm.id];
                      updateForm("jobModelIds", newIds);
                      const hasPresential = (lookups.jobModels || []).some(
                        m => newIds.includes(m.id) && ['HYBRID', 'IN_PERSON'].includes(m.workModality)
                      );
                      if (!hasPresential) updateForm("preferredRegions", []);
                    }}>{jm.name}</Badge>
                );
              })}
            </div>
          </div>
        </div>

        {/* Regions (conditional) */}
        {showRegions && (
          <div className="space-y-3 mt-4 pt-4 border-t">
            <Label>Regiões de interesse</Label>
            <RegionAdder onAdd={(region) => updateForm("preferredRegions", [...(formData.preferredRegions || []), region])} />
            {(formData.preferredRegions || []).length > 0 && (
              <div className="space-y-2 mt-2">
                {formData.preferredRegions.map((r, i) => (
                  <div key={i} className="flex items-center justify-between rounded-md border px-3 py-2">
                    <div className="flex flex-col">
                      <span className="text-sm font-medium">{r.city ? formatCityName(r.city) : "Todas as cidades"}</span>
                      <span className="text-xs text-muted-foreground">{STATES.find(s => s.value === r.state)?.label || r.state}</span>
                    </div>
                    <button type="button" className="text-muted-foreground hover:text-destructive"
                      onClick={() => updateForm("preferredRegions", formData.preferredRegions.filter((_, j) => j !== i))}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </motion.div>
    );
  };

  // ══════════════════════════════════════════════════
  // ══  RENDER  ═════════════════════════════════════
  // ══════════════════════════════════════════════════

  const isWelcome = step === 0;
  const isCompletion = step === 6;
  const showBackButton = !(isWelcome || isCompletion);
  const showNextButton = !isCompletion;

  let nextLabel = "Começar";
  let nextDisabled = false;
  if (step > 0 && step <= 5) {
    if (phase === "transition") {
      nextLabel = "Continuar";
    } else {
      nextLabel = step === 5 ? "Concluir" : "Próximo";
      nextDisabled = !isStepValid(step);
    }
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      {/* Step Indicator */}
      {step >= 1 && step <= 5 && StepIndicator()}

      <div>
          <AnimatePresence mode="wait">
            {/* Welcome */}
            {isWelcome && TransitionScreen({ transitionIndex: 0 })}

            {/* Step 1 — no transition before form (goes straight from welcome) */}
            {step === 1 && phase === "form" && Step1Personal()}

            {/* Steps 2-5 — transition screens shown AFTER completing previous step */}
            {step >= 2 && step <= 5 && phase === "transition" &&
              TransitionScreen({ transitionIndex: step - 1 })}
            {step === 2 && phase === "form" && Step2Experience()}
            {step === 3 && phase === "form" && Step3Knowledge()}
            {step === 4 && phase === "form" && Step4Purpose()}
            {step === 5 && phase === "form" && Step5Preferences()}

            {/* Completion */}
            {isCompletion && TransitionScreen({ transitionIndex: 5 })}
          </AnimatePresence>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t">
            {showBackButton ? (
              <Button variant="ghost" onClick={handleBack} className="gap-2">
                <ArrowLeft className="w-4 h-4" /> Voltar
              </Button>
            ) : <div />}

            {isCompletion ? (
              <Button onClick={handleFinish} className="rounded-full gap-2 px-6"
                style={{ background: "#00DCA4", color: "#003326" }}>
                Explorar a plataforma <ArrowRight className="w-4 h-4" />
              </Button>
            ) : (
              <Button onClick={handleNext} disabled={nextDisabled || saving}
                className="rounded-full gap-2 px-6"
                style={{ background: nextDisabled ? "#e5e7eb" : "#00DCA4", color: nextDisabled ? "#9ca3af" : "#003326" }}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                  <>{nextLabel} <ArrowRight className="w-4 h-4" /></>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
  );
}
